// ─── Tree construction ────────────────────────────────────────────────
export function buildTree(nodes) {
    const nodeMap = new Map();
    const children = new Map();
    const parent = new Map();
    const roots = [];
    for (const node of nodes) {
        nodeMap.set(node.id, node);
    }
    for (const node of nodes) {
        if (node.dependsOn !== null && nodeMap.has(node.dependsOn)) {
            parent.set(node.id, node.dependsOn);
            const siblings = children.get(node.dependsOn) ?? [];
            siblings.push(node.id);
            children.set(node.dependsOn, siblings);
        }
        else {
            roots.push(node.id);
        }
    }
    return { nodes: nodeMap, children, parent, roots };
}
// ─── Helpers ──────────────────────────────────────────────────────────
export function isBranchPoint(ctx, nodeId) {
    return (ctx.children.get(nodeId)?.length ?? 0) >= 2;
}
export function isLeaf(ctx, nodeId) {
    return (ctx.children.get(nodeId)?.length ?? 0) === 0;
}
/**
 * The harvest's receive root, if any: the root operation whose kind is
 * `RECEIVE`. Its presence is the discriminator between a forward-enforced
 * harvest (receive-rooted) and a grandfathered legacy one.
 */
export function receiveRoot(ctx) {
    for (const id of ctx.roots) {
        if (ctx.nodes.get(id)?.kind === "RECEIVE")
            return id;
    }
    return null;
}
export function hasReceiveRoot(ctx) {
    return receiveRoot(ctx) !== null;
}
/**
 * Why the tree cannot stand, if it cannot.
 *
 * The single-root rule was enforced edit by edit, which left the shapes no edit
 * produced — a protocol authored with an ordinary step at the top, or one whose
 * receive step was added second — accepted in full. So the invariant is stated
 * once here and asserted against the finished tree after any structural change,
 * rather than being inferred from each move that could break it.
 *
 * Trees carrying no `RECEIVE` node at all are left alone: legacy harvests and
 * protocols predate the kind, and rejecting them would make them uneditable.
 */
export function validateReceiveRoot(ctx) {
    const receives = [...ctx.nodes.values()].filter((n) => n.kind === "RECEIVE");
    if (receives.length === 0)
        return null;
    if (receives.length > 1) {
        return "A harvest takes material in at one place, so it has one receive operation.";
    }
    const receive = receives[0];
    if (receive.dependsOn !== null && ctx.nodes.has(receive.dependsOn)) {
        return "The receive operation is where material enters, so nothing may feed it.";
    }
    if (ctx.roots.length > 1) {
        return "Everything hangs beneath the receive operation, so the plan has one starting point.";
    }
    return null;
}
export function getDescendants(ctx, nodeId) {
    const result = [];
    const stack = [...(ctx.children.get(nodeId) ?? [])];
    while (stack.length > 0) {
        const id = stack.pop();
        result.push(id);
        const kids = ctx.children.get(id);
        if (kids)
            stack.push(...kids);
    }
    return result;
}
/**
 * What a node can actually emit.
 *
 * A `PASSTHROUGH` operation is generic over its material: cold holding, transfer
 * and weighing all put out whatever went in, so there is nothing for the
 * template to declare and `outputMaterials` is empty. Its output is the material
 * it was wired to consume — empty while it is a root or unwired, since nothing
 * has fed it yet.
 *
 * Every consumer of a node's outputs goes through here — wiring validation, the
 * DAG connection layout, the planner grid, the batch-type pickers — so the rule
 * lives in one place. Prefer this over reading `outputMaterials` directly.
 */
export function effectiveOutputMaterialsOf(node) {
    if (!node)
        return [];
    if (node.kind === "PASSTHROUGH") {
        return node.dependsOnMaterial == null ? [] : [node.dependsOnMaterial];
    }
    return node.outputMaterials ?? [];
}
/** `effectiveOutputMaterialsOf` for a node addressed by id within a tree. */
export function effectiveOutputMaterials(ctx, nodeId) {
    return effectiveOutputMaterialsOf(ctx.nodes.get(nodeId));
}
// ─── Validators ───────────────────────────────────────────────────────
/**
 * A node is structurally editable (movable / deletable / reparentable) only if
 * it has no recorded data and none of its descendants do. This is the single
 * source of truth shared by the frontend capabilities and the backend mutation
 * guards.
 */
export function isStructurallyEditable(ctx, nodeId) {
    const node = ctx.nodes.get(nodeId);
    if (!node || node.hasRecordedData)
        return false;
    // The receive root is structural — never movable, deletable, or reparentable.
    if (node.kind === "RECEIVE")
        return false;
    return getDescendants(ctx, nodeId).every((id) => ctx.nodes.get(id)?.hasRecordedData === false);
}
export function canMoveUp(ctx, nodeId) {
    if (!isStructurallyEditable(ctx, nodeId))
        return false;
    if (isBranchPoint(ctx, nodeId))
        return false;
    const parentId = ctx.parent.get(nodeId);
    if (parentId === undefined)
        return false; // root
    const parentNode = ctx.nodes.get(parentId);
    const parentChildCount = ctx.children.get(parentId)?.length ?? 0;
    // Never promote a node above the receive root: that would make it a second
    // root (linear swap) or displace the sanctioned inlet.
    if (parentNode.kind === "RECEIVE")
        return false;
    if (parentChildCount >= 2) {
        // Extract from branch (Rule 3) — allowed
        return true;
    }
    // Linear swap (Rule 1) — parent must have no recorded data
    return !parentNode.hasRecordedData;
}
export function canMoveDown(ctx, nodeId) {
    if (!isStructurallyEditable(ctx, nodeId))
        return false;
    if (isBranchPoint(ctx, nodeId))
        return false;
    const childIds = ctx.children.get(nodeId);
    if (!childIds || childIds.length !== 1)
        return false;
    const childId = childIds[0];
    const child = ctx.nodes.get(childId);
    if (child.hasRecordedData)
        return false;
    if (isBranchPoint(ctx, childId))
        return false;
    return true;
}
export function canDelete(ctx, nodeId) {
    return isStructurallyEditable(ctx, nodeId);
}
/**
 * Validates a free reparent: move `nodeId` (with its whole subtree) under
 * `newParentId` via the `newMaterial` output. `newParentId === null` makes the
 * node a root. The moved subtree must have no recorded data; the new parent may
 * have data.
 */
export function canReparent(ctx, nodeId, newParentId, newMaterial) {
    if (!ctx.nodes.has(nodeId))
        return false;
    if (!isStructurallyEditable(ctx, nodeId))
        return false;
    // Becoming a root — allowed only when no receive root exists (single-root
    // invariant). Legacy harvests without a receive root keep the free behavior.
    if (newParentId === null)
        return !hasReceiveRoot(ctx);
    // Cannot parent onto itself.
    if (newParentId === nodeId)
        return false;
    const newParent = ctx.nodes.get(newParentId);
    if (!newParent)
        return false;
    // Cycle: the new parent must not live inside the moved subtree.
    if (getDescendants(ctx, nodeId).includes(newParentId))
        return false;
    // Batch-type validity: the new parent must actually produce `newMaterial`.
    if (newMaterial !== null &&
        !effectiveOutputMaterials(ctx, newParentId).includes(newMaterial)) {
        return false;
    }
    return true;
}
/**
 * Whether swapping `nodeId`'s template leaves every child still fed. The new
 * template's declared outputs come from the caller; pass its `newKind` so a
 * swap to a `PASSTHROUGH` template is judged against the material the node
 * inherits rather than against its (empty) declaration.
 */
export function canSwapTemplate(ctx, nodeId, newOutputBatchTypes, newKind) {
    const childIds = ctx.children.get(nodeId) ?? [];
    const missingBatchTypes = [];
    const node = ctx.nodes.get(nodeId);
    const effectiveOutputs = effectiveOutputMaterialsOf({
        kind: newKind,
        dependsOnMaterial: node?.dependsOnMaterial ?? null,
        outputMaterials: newOutputBatchTypes,
    });
    for (const childId of childIds) {
        const child = ctx.nodes.get(childId);
        if (child.dependsOnMaterial &&
            !effectiveOutputs.includes(child.dependsOnMaterial)) {
            missingBatchTypes.push(child.dependsOnMaterial);
        }
    }
    return {
        valid: missingBatchTypes.length === 0,
        missingBatchTypes,
    };
}
// ─── Operations ───────────────────────────────────────────────────────
export function moveUp(ctx, nodeId) {
    const parentId = ctx.parent.get(nodeId);
    const node = ctx.nodes.get(nodeId);
    const parentNode = ctx.nodes.get(parentId);
    const grandparentId = ctx.parent.get(parentId) ?? null;
    const parentChildCount = ctx.children.get(parentId)?.length ?? 0;
    if (parentChildCount >= 2) {
        // Case B — Extract from branch
        const depChanges = [
            {
                operationId: nodeId,
                dependsOn: grandparentId,
                dependsOnMaterial: parentNode.dependsOnMaterial,
            },
            {
                operationId: parentId,
                dependsOn: nodeId,
                dependsOnMaterial: null,
            },
        ];
        return { dependencyChanges: depChanges };
    }
    // Case A — Linear swap
    const nodeChildIds = ctx.children.get(nodeId) ?? [];
    const depChanges = [
        // N takes P's position
        {
            operationId: nodeId,
            dependsOn: grandparentId,
            dependsOnMaterial: parentNode.dependsOnMaterial,
        },
        // P becomes child of N
        {
            operationId: parentId,
            dependsOn: nodeId,
            dependsOnMaterial: node.dependsOnMaterial,
        },
    ];
    // Re-parent N's children to P
    for (const childId of nodeChildIds) {
        const child = ctx.nodes.get(childId);
        depChanges.push({
            operationId: childId,
            dependsOn: parentId,
            dependsOnMaterial: child.dependsOnMaterial,
        });
    }
    return { dependencyChanges: depChanges };
}
export function moveDown(ctx, nodeId) {
    const childIds = ctx.children.get(nodeId);
    return moveUp(ctx, childIds[0]);
}
/**
 * Subtree-preserving reparent: only the moved node's own dependency changes.
 * Its children stay attached to it, so the entire subtree moves as a unit.
 */
export function reparent(_ctx, nodeId, newParentId, newMaterial) {
    return {
        dependencyChanges: [
            {
                operationId: nodeId,
                dependsOn: newParentId,
                dependsOnMaterial: newParentId === null ? null : newMaterial,
            },
        ],
    };
}
export function computeDelete(ctx, nodeId) {
    const node = ctx.nodes.get(nodeId);
    const childIds = ctx.children.get(nodeId) ?? [];
    if (childIds.length === 0) {
        // Leaf
        return {
            idsToDelete: [nodeId],
            reconnection: null,
            requiresConfirmation: false,
        };
    }
    if (childIds.length === 1) {
        // Linear — reconnect child to parent
        const childId = childIds[0];
        return {
            idsToDelete: [nodeId],
            reconnection: {
                operationId: childId,
                dependsOn: node.dependsOn,
                dependsOnMaterial: node.dependsOnMaterial,
            },
            requiresConfirmation: false,
        };
    }
    // Branch point — delete entire subtree
    return {
        idsToDelete: [nodeId, ...getDescendants(ctx, nodeId)],
        reconnection: null,
        requiresConfirmation: true,
    };
}
/**
 * Process-flow order: depth-first pre-order of the dependency tree, siblings by
 * id. Matches the plan-view step order. Cycle-safe (unreached nodes appended).
 */
export function flowOrder(ctx) {
    const ordered = [];
    const seen = new Set();
    const visit = (id) => {
        if (seen.has(id))
            return;
        seen.add(id);
        ordered.push(id);
        const kids = [...(ctx.children.get(id) ?? [])].sort((a, b) => a - b);
        for (const kid of kids)
            visit(kid);
    };
    for (const root of [...ctx.roots].sort((a, b) => a - b))
        visit(root);
    for (const id of [...ctx.nodes.keys()].sort((a, b) => a - b))
        visit(id);
    return ordered;
}
export function recomputeDepths(ctx) {
    const depths = new Map();
    const queue = ctx.roots.map((id) => ({ id, depth: 0 }));
    while (queue.length > 0) {
        const { id, depth } = queue.shift();
        depths.set(id, depth);
        const kids = ctx.children.get(id);
        if (kids) {
            for (const kid of kids) {
                queue.push({ id: kid, depth: depth + 1 });
            }
        }
    }
    return depths;
}
//# sourceMappingURL=harvest-tree.js.map